import type { LocationIdentifier, OrganizationIdentifier, PractitionerIdentifier } from "~/types/api";

export const QUERY_MESSAGES = "messages";
export const QUERY_PATIENT_MESSAGES = "patientMessages";
export const QUERY_MESSAGE_DETAIL = "messageDetail";
export const QUERY_MESSAGES_COUNT = "unread-message-counts";
export const QUERY_MY_DAY = "queryMyDay";
export const QUERY_PATIENT = "queryPatient";
export const QUERY_PROFILE = "queryProfile";
export const QUERY_PRACTITIONER_PROFILE = "queryPractitionerProfile";

export type PractitionerProfileQueryKey = [
  typeof QUERY_PROFILE,
  typeof QUERY_PRACTITIONER_PROFILE,
  PractitionerIdentifier,
  OrganizationIdentifier | null,
  LocationIdentifier | null,
];

export const practitionerProfileQueryKey = (
  practitionerId: PractitionerIdentifier,
  organizationId: OrganizationIdentifier | null | undefined,
  locationId: LocationIdentifier | null | undefined,
): PractitionerProfileQueryKey => [
  QUERY_PROFILE,
  QUERY_PRACTITIONER_PROFILE,
  practitionerId,
  organizationId ?? null,
  locationId ?? null,
];
export const QUERY_PATIENT_PROFILE = "queryPatientProfile";
export const QUERY_PATIENT_EVENTS = "queryPatientEvents";
export const QUERY_LOCATIONS = "queryLocations";
export const QUERY_EMPLOYEES = "queryEmployees";
export const QUERY_BULK_EMPLOYEE_ORGANIZATIONS = "queryBulkEmployeeOrganizations";
export const QUERY_BULK_EMPLOYEE_OPTIONS = "queryBulkEmployeeOptions";
export const QUERY_ASSIGNABLE_EMPLOYEES = "queryAssignableEmployees";
export const QUERY_REVENUE_CLINICIANS = "queryRevenueClinicians";
export const QUERY_DECLARATION_TREATMENT_PRACTITIONERS = "queryDeclarationTreatmentPractitioners";
export const QUERY_DECLARATION_CORRECTION_PROVIDER_OPTIONS = "queryDeclarationCorrectionProviderOptions";
export const QUERY_DECLARATION_PROVIDER_OPTIONS = "queryDeclarationProviderOptions";
export const QUERY_INVOICE_RETURN_PROVIDER_OPTIONS = "queryInvoiceReturnProviderOptions";
export const QUERY_INTAKE = "queryIntake";
export const QUERY_INTAKE_JOURNALS = "queryIntakeJournals";
export const QUERY_TREATMENT = "queryTreatment";
export const QUERY_CHECKPOINTS = "queryCheckpoints";
export const QUERY_WORK_SCHEDULES = "queryWorkSchedules";
export const QUERY_EMPLOYEE_LOCATIONS = "queryEmployeeLocations";
export const QUERY_TREATMENT_JOURNALS = "queryTreatmentJournals";
export const QUERY_DCSPH_CODE = "queryDcsphCode";
export const QUERY_CSI_CODE = "queryCsiCode";
export const QUERY_SELECTABLE_THIRD_PARTIES = "querySelectableThirdParties";
export const QUERY_MY_ORGANIZATION_THIRD_PARTIES = "queryMyOrganizationThirdParties";
export const QUERY_SELECTABLE_REFERRERS = "querySelectableReferrers";
export const QUERY_MY_ORGANIZATION_REFERRERS = "queryMyOrganizationReferrers";
export const QUERY_PLANNER = "queryPlanner";
export const QUERY_TREATMENT_DOCUMENTS = "queryTreatmentDocuments";
export const QUERY_TREATMENT_REFERRAL_ATTACHMENTS = "queryTreatmentReferralAttachments";
export const QUERY_TREATMENT_NOTES = "queryTreatmentNotes";
export const QUERY_PATIENT_NOTES = "queryPatientNotes";
export const QUERY_INTAKE_QUESTIONNAIRE_RESULTS = "intakeQuestionnaireResults";
export const QUERY_INTAKE_QUESTIONNAIRE_ANSWERS = "intakeQuestionnaireAnswers";
export const QUERY_INTAKE_QUESTIONNAIRE_FORM = "intakeQuestionnaireForm";
export const QUERY_INTAKE_QUESTIONNAIRE_DELIVERY = "queryIntakeQuestionnaireDelivery";
export const QUERY_FLAGGED_PATIENT_NOTES = "queryFlaggedPatientNotes";
export const QUERY_PATIENT_ACTIVITY_LOGS = "queryPatientActivityLogs";
export const QUERY_PATIENT_EMAIL_COMMUNICATIONS = "queryPatientEmailCommunications";
export const QUERY_OPEN_TRAJECTORIES = "queryOpenTrajectories";
export const QUERY_MANAGED_TRAJECTORIES = "queryManagedTrajectories";
export const QUERY_ONBOARDING_STATUS = "queryOnboardingStatus";
export const QUERY_ORGANIZATION_HEALTH_DASHBOARD = "queryOrganizationHealthDashboard";
export const QUERY_LOCATION_HEALTH_DASHBOARD = "queryLocationHealthDashboard";
export const QUERY_ORGANIZATION = "queryOrganization";
export const QUERY_ORGANIZATION_LOCATIONS = "queryOrganizationLocations";
export const QUERY_REFERRAL_DETAIL = "queryReferralDetail";
export const QUERY_REFERRAL_REFERRER_CANDIDATES = "queryReferralReferrerCandidates";
export const QUERY_REFERRAL_LINKABLE_TRAJECTORIES = "queryReferralLinkableTrajectories";
export const REFERRAL_LINKABLE_TRAJECTORIES_STALE_TIME = 10 * 1000;
export const QUERY_REFERRALS_NEW_COUNT = "queryReferralsNewCount";
export const QUERY_LOCATION_ZORGDOMEIN_ACTIVATION = "queryLocationZorgdomeinActivation";
export const QUERY_PRACTICES = "queryPractices";
export const QUERY_PRACTICE = "queryPractice";
export const QUERY_PRACTICE_AGB_REGISTER = "queryPracticeAgbRegister";
export const QUERY_PRACTICE_AGB_REGISTER_RELATIONS = "queryPracticeAgbRegisterRelations";
export const QUERY_EMPLOYEE_AGB_REGISTER = "queryEmployeeAgbRegister";
export const QUERY_REFERRER_AGB_REGISTER = "queryReferrerAgbRegister";
export const QUERY_ZORGMAIL_ADDRESS_BOOK = "queryZorgmailAddressBook";
export const QUERY_PATIENT_ZORGMAIL_MESSAGES = "queryPatientZorgmailMessages";
export const QUERY_PATIENT_DOCUMENTS = "queryPatientDocuments";
export const QUERY_PATIENT_ZORGMAIL_MESSAGE_DETAIL = "queryPatientZorgmailMessageDetail";
export const QUERY_ZORGMAIL_SEND_PREVIEW = "queryZorgmailSendPreview";
export const QUERY_ZORGMAIL_INBOX_MESSAGE = "queryZorgmailInboxMessage";
export const QUERY_ZORGMAIL_UNLINKED_COUNT = "queryZorgmailUnlinkedCount";
export const QUERY_PRACTICE_LDF_BACKFILL = "queryPracticeLdfBackfill";
export const QUERY_PRACTICE_LDF_SUBMISSIONS = "queryPracticeLdfSubmissions";
export const QUERY_PRACTICE_ZORGTOPICS_DELIVERY_PREVIEW = "queryPracticeZorgtopicsDeliveryPreview";
